import React, { useState, useEffect } from 'react';
import { useFormPopup } from '../contexts/FormPopupContext';
import { DynamicForm } from './DynamicForm';
import { API_BASE } from '../config/api';

export const FormPopupModal = () => {
  const { isOpen, formSlug, closeFormPopup, openFormPopup } = useFormPopup();
  const [formConfig, setFormConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const backendUrl = API_BASE;

  // Set global function reference
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.openFormPopup = openFormPopup;
    }
    
    return () => {
      if (typeof window !== 'undefined') {
        window.openFormPopup = null;
      }
    };
  }, [openFormPopup]);

  // Fetch form config when slug changes
  useEffect(() => {
    if (isOpen && formSlug) {
      fetchFormConfig();
    } else {
      setFormConfig(null);
      setError(null);
    }
  }, [isOpen, formSlug]);

  const fetchFormConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${backendUrl}/api/public/forms/${formSlug}`);
      
      if (!response.ok) {
        throw new Error('Form not found');
      }
      
      const data = await response.json();
      setFormConfig(data.form);
    } catch (err) {
      console.error('Failed to fetch form:', err);
      setError(err.message || 'Failed to load form');
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    // Close modal if clicking on overlay (not modal content)
    if (e.target === e.currentTarget) {
      closeFormPopup();
    }
  };

  const handleFormSuccess = () => {
    // Optionally auto-close after successful submission
    // For now, keep modal open to show success message
    // Users can close manually via X button
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 overflow-y-auto"
      onClick={handleOverlayClick}
      data-testid="form-popup-overlay"
    >
      <div
        className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
        data-testid="form-popup-modal"
      >
        {/* Close Button */}
        <button
          onClick={closeFormPopup}
          className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
          aria-label="Close"
          data-testid="form-popup-close"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Modal Content */}
        <div className="p-6 md:p-8">
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block w-8 h-8 border-3 border-[#1E40AF] border-t-transparent rounded-full animate-spin mb-4"></div>
              <p className="text-[#6B7280]">Loading form...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <p className="text-red-600 font-semibold mb-2">Error Loading Form</p>
              <p className="text-[#6B7280] text-sm mb-4">{error}</p>
              <button
                onClick={closeFormPopup}
                className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          )}

          {formConfig && !loading && !error && (
            <>
              {/* Form Header */}
              <div className="text-center mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-[#111827] mb-2">
                  {formConfig.title}
                </h2>
                {formConfig.description && (
                  <p className="text-sm md:text-base text-[#6B7280]">
                    {formConfig.description}
                  </p>
                )}
              </div>

              {/* Dynamic Form Component */}
              <DynamicForm
                formSlug={formSlug}
                isPopup={true}
                onSuccess={handleFormSuccess}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
